import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ChartLineUp, ChartLine, PlayCircle, X as XIcon,
  ArrowClockwise, CheckCircle, EnvelopeSimple, CaretRight,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * Compact card for the pilot-facing Weekly Business Brief.
 * Lives inside Settings → Pricing (so the pilot can toggle it near their other
 * outbound email prefs). Ships:
 *   - toggle: opt-in / opt-out
 *   - "Preview this week" → modal with the AI-drafted brief on the pilot's own numbers
 *   - "View history" → modal with the last ~12 briefs sent to this pilot
 */
export default function PilotDigestCard() {
  const [optedOut, setOptedOut] = useState(false);
  const [savingOptOut, setSavingOptOut] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);

  const loadOptOutState = async () => {
    try {
      const { data } = await client.get("/auth/me");
      setOptedOut(Boolean(data?.weekly_digest_opt_out));
    } catch {
      /* silent — if /me fails the page will already have surfaced it */
    }
  };
  useEffect(() => { loadOptOutState(); }, []);

  const toggleOptOut = async () => {
    const nextOptedOut = !optedOut;
    setSavingOptOut(true);
    try {
      if (nextOptedOut) {
        await client.post("/pilot-digest/opt-out");
        toast.success("Weekly brief turned off. No more Monday emails.");
      } else {
        await client.post("/pilot-digest/opt-in");
        toast.success("Weekly brief turned on. Next brief lands Monday 8am CT.");
      }
      setOptedOut(nextOptedOut);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update preference");
    } finally {
      setSavingOptOut(false);
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const { data } = await client.get("/pilot-digest/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setPreview({ status: "error", error: e?.response?.data?.detail || "Unknown error" });
    } finally {
      setPreviewLoading(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setSelectedHistoryId(null);
    try {
      const { data } = await client.get("/pilot-digest/history");
      setHistory(data?.digests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "History failed");
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const selectedHistoryItem = history.find((d) => d.id === selectedHistoryId);

  return (
    <div className="card-surface p-6" data-testid="pilot-digest-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 mb-2">
            <ChartLineUp size={13} /> Weekly business brief
          </div>
          <h2 className="text-lg font-semibold text-white">Monday-morning AI digest of your numbers</h2>
          <p className="text-neutral-400 text-sm mt-2 leading-relaxed">
            Every Monday at 8am Central, we email you a 3-paragraph plain-English brief of last week's
            revenue, best driver, top lane, and how much last week's diesel move helped or hurt your
            margin. Written by GPT-5.2 on YOUR data — no generic advice.
          </p>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <button
            onClick={toggleOptOut}
            disabled={savingOptOut}
            data-testid="pilot-digest-toggle"
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${
              optedOut ? "bg-white/10" : "bg-[#FF5A1F]"
            }`}
            aria-label={optedOut ? "Turn weekly brief on" : "Turn weekly brief off"}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                optedOut ? "translate-x-1" : "translate-x-6"
              }`}
            />
          </button>
          <div className="text-[11px] text-neutral-500 font-mono">
            {optedOut ? "OFF" : "ON"}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={openPreview}
          data-testid="pilot-digest-preview-btn"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] text-white text-xs font-medium px-3 py-2 hover:bg-white/[0.06] transition"
        >
          <PlayCircle size={13} weight="fill" /> Preview this week
        </button>
        <button
          onClick={openHistory}
          data-testid="pilot-digest-history-btn"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] text-white text-xs font-medium px-3 py-2 hover:bg-white/[0.06] transition"
        >
          <ChartLine size={13} /> View history
        </button>
      </div>

      {previewOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="pilot-digest-preview-modal">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">This week's preview</div>
                <div className="text-white font-semibold">Weekly business brief</div>
              </div>
              <button
                onClick={() => setPreviewOpen(false)}
                data-testid="pilot-digest-preview-close"
                className="text-neutral-500 hover:text-white transition"
              >
                <XIcon size={18} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {previewLoading ? (
                <div className="text-neutral-500 text-sm py-6 text-center inline-flex items-center gap-2 justify-center w-full">
                  <ArrowClockwise size={13} className="animate-spin" /> Drafting your brief with GPT-5.2…
                </div>
              ) : preview?.status === "skipped_no_data" ? (
                <div className="text-neutral-400 text-sm">
                  <p className="mb-2">
                    <span className="text-amber-400 font-semibold">Not enough data yet.</span>{" "}
                    {preview?.reason}
                  </p>
                  <p className="text-neutral-500 text-xs">
                    Add some delivered loads and check back next week — briefs need at least 1 delivered
                    load OR 90 days of lane history.
                  </p>
                </div>
              ) : preview?.status === "error" ? (
                <div className="text-red-400 text-sm">{preview?.error}</div>
              ) : preview ? (
                <div>
                  {preview.context?.week_stats && (
                    <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <StatCell label="Loads" value={preview.context.week_stats.loads} />
                      <StatCell label="Revenue" value={`$${(preview.context.week_stats.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
                      <StatCell label="Loaded Mi" value={(preview.context.week_stats.loaded_miles || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} />
                      <StatCell label="$/mile" value={`$${preview.context.week_stats.per_mile ?? 0}`} />
                    </div>
                  )}
                  <div className="text-xs uppercase tracking-widest text-[#FF5A1F] font-semibold mb-2">Subject</div>
                  <div className="text-white text-sm mb-4">{preview.subject}</div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Body</div>
                  <div className="text-neutral-200 text-sm whitespace-pre-wrap leading-relaxed">
                    {preview.body}
                  </div>
                  {preview.context?.fuel?.wow_change_usd != null && (
                    <div className="mt-5 pt-4 border-t border-white/10 text-[11px] text-neutral-500 leading-relaxed">
                      Fuel context: diesel ${preview.context.fuel.current_price?.toFixed(3)}/gal
                      ({preview.context.fuel.wow_change_usd > 0 ? "▲" : "▼"} $
                      {Math.abs(preview.context.fuel.wow_change_usd).toFixed(3)}/gal WoW)
                      → margin impact {preview.context.fuel.fuel_margin_impact_usd > 0 ? "-" : "+"}$
                      {Math.abs(preview.context.fuel.fuel_margin_impact_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      {preview.context.fuel.fuel_margin_impact_pct != null && (
                        <> ({preview.context.fuel.fuel_margin_impact_pct > 0 ? "+" : ""}{preview.context.fuel.fuel_margin_impact_pct}pp)</>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            <div className="px-6 py-3 border-t border-white/10 flex justify-end gap-2 shrink-0">
              <button
                onClick={() => setPreviewOpen(false)}
                data-testid="pilot-digest-preview-dismiss"
                className="rounded-full border border-white/10 text-white text-xs font-medium px-4 py-2 hover:bg-white/[0.06] transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {historyOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="pilot-digest-history-modal">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between shrink-0">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Past briefs</div>
                <div className="text-white font-semibold">Weekly brief history</div>
              </div>
              <button
                onClick={() => setHistoryOpen(false)}
                data-testid="pilot-digest-history-close"
                className="text-neutral-500 hover:text-white transition"
              >
                <XIcon size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden flex">
              <div className="w-1/3 border-r border-white/10 overflow-y-auto">
                {historyLoading ? (
                  <div className="text-neutral-500 text-sm py-6 text-center">Loading…</div>
                ) : history.length === 0 ? (
                  <div className="text-neutral-500 text-sm p-4">
                    No briefs sent yet. Your first one lands next Monday.
                  </div>
                ) : (
                  <ul className="divide-y divide-white/[0.06]">
                    {history.map((d) => {
                      const active = d.id === selectedHistoryId;
                      const sent = d.status === "sent";
                      return (
                        <li key={d.id}>
                          <button
                            onClick={() => setSelectedHistoryId(d.id)}
                            data-testid={`pilot-digest-history-item-${d.id}`}
                            className={`w-full text-left px-4 py-3 text-sm transition ${
                              active ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">
                                {d.week_key || "—"}
                              </span>
                              <span className={`text-[10px] font-semibold ${
                                sent ? "text-emerald-400" : "text-neutral-500"
                              }`}>
                                {sent ? "SENT" : (d.status || "").toUpperCase()}
                              </span>
                            </div>
                            <div className="text-white text-sm mt-1 line-clamp-2 leading-snug">
                              {d.subject || "(no subject)"}
                            </div>
                            {sent && (d.open_count > 0 || d.click_count > 0) && (
                              <div className="mt-1 text-[10px] text-neutral-500">
                                {d.open_count > 0 && <>👁 {d.open_count}</>}
                                {d.click_count > 0 && <>{d.open_count > 0 && " · "}🖱 {d.click_count}</>}
                              </div>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                {selectedHistoryItem ? (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
                      Week {selectedHistoryItem.week_key}
                    </div>
                    <div className="text-white font-semibold mb-3">{selectedHistoryItem.subject}</div>
                    <div className="text-neutral-200 text-sm whitespace-pre-wrap leading-relaxed">
                      {selectedHistoryItem.body || "(no body — this was a scheduler dry-run)"}
                    </div>
                    <div className="mt-5 pt-4 border-t border-white/10 text-[11px] text-neutral-500 flex flex-wrap gap-x-4 gap-y-1">
                      <span>Sent: {selectedHistoryItem.sent_at || selectedHistoryItem.created_at?.slice(0, 19)}</span>
                      <span>Provider: <span className="font-mono">{selectedHistoryItem.provider || "—"}</span></span>
                      {selectedHistoryItem.open_count > 0 && <span>Opens: {selectedHistoryItem.open_count}</span>}
                      {selectedHistoryItem.click_count > 0 && <span>Clicks: {selectedHistoryItem.click_count}</span>}
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-neutral-500">
                    <EnvelopeSimple size={32} className="opacity-40" />
                    <div className="text-sm">Pick a brief to read it.</div>
                    <div className="text-xs opacity-70">Newest at the top.</div>
                    <CaretRight size={14} className="opacity-40 mt-2" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-white font-semibold text-sm mt-0.5">{value}</div>
    </div>
  );
}
